import React, { useEffect } from 'react';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { isValid } from 'date-fns';
import useDeleteSingleSimulation from 'Hooks/query-hooks/simulations/useDeleteSingleSimulation';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import { shouldShowMerchants } from 'Selectors';
import { useDispatch } from 'react-redux';
import { startCase } from 'lodash';
import { setDeleteSimulationModalOpen } from 'Store';
import { getCustomUserName } from 'Utils';
import { DateService } from 'Services';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import styles from './DeleteSimulationModal.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    rowValues: {
        id: number;
        createdAt: string;
        createdBy: string;
        start: string;
        scope: string;
        sandbox: string;
        simulationType: string;
        duration: string;
        compare: boolean;
    };
};

const DeleteSimulationModal = ({ isOpen, onClose, rowValues, onSuccess }: Props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { users } = useGetReduxUsers();
    const { id, createdAt, createdBy } = rowValues;
    const customUserName = getCustomUserName(createdBy, users);

    const reportCreatedAt = isValid(new Date(createdAt)) ? DateService.getDate(createdAt) : '';
    const reportCreatedBy = createdBy && createdBy !== '' ? customUserName : '';

    const deleteSingleSimulation = useDeleteSingleSimulation(id);

    const handleOnAction = () => {
        deleteSingleSimulation.mutate();
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Manage Simulations - Delete',
                text: 'Simulations - Delete'
            }
        });
    };

    useEffect(() => {
        if (deleteSingleSimulation?.isSuccess) {
            onSuccess?.();
            onClose();
            deleteSingleSimulation.reset();
        } else if (deleteSingleSimulation?.error) {
            onClose();
            deleteSingleSimulation.reset();
        }
    }, [deleteSingleSimulation, onClose, onSuccess]);

    useEffect(() => {
        dispatch(setDeleteSimulationModalOpen(isOpen));
    }, [isOpen, dispatch]);

    return (
        <Modal
            dataTestId='delete-simulation-modal'
            isOpen={isOpen}
            closeModal={onClose}
            status='warning'
            containerClass={styles.modalContainer}
        >
            <Modal.Title
                id='delete-simulation-title'
                title={t('Delete Simulation')}
                statusIcon='warning'
                onClose={onClose}
            />
            <div className={styles.deleteWarningContent}>
                <div className={styles.reportInfo}>
                    <span>
                        {`${t('Are you sure you want to delete this simulation')} ${t('created')} ${t('on')}`}&nbsp;
                    </span>
                    <span className={styles.boldedFont}>{reportCreatedAt}&nbsp;</span>
                    <span>{`${t('by')}`}&nbsp;</span>
                    <span className={styles.boldedFont}>{reportCreatedBy}</span>
                    <span> {t('?')}</span>
                </div>
                <div>
                    {t('Simulation Summary')}
                    <ul className={styles.simulationSummary}>
                        <li>{t(rowValues.simulationType)}</li>
                        <li>{rowValues.sandbox}</li>
                        {shouldShowMerchants() ? <li>{rowValues.scope}</li> : null}
                        {rowValues.compare ? <li>{t('ComparedToDeployedVersion')} </li> : null}
                        <li>
                            {t('simulationSummary.start', {
                                date: DateService.getDate(rowValues.start.toString()),
                                time: DateService.getTime(rowValues.start.toString())
                            })}
                        </li>
                        <li>{t('simulationSummary.duration', { duration: startCase(rowValues.duration) })}</li>
                    </ul>
                </div>
                {t('This cannot be undone')}
            </div>
            <Modal.Footer
                id='delete-simulation-footer'
                onAction={handleOnAction}
                onActionText={t('Delete')}
                onClose={onClose}
                onCloseText={t('Cancel')}
            />
        </Modal>
    );
};

export default DeleteSimulationModal;
